import { toast } from 'react-toastify';
import React, { useState, useRef, useEffect } from 'react';
import { FileText, Eye, Download, Trash2, Pencil } from 'lucide-react';
import { uploadFile, getFiles, deleteFile, updateFile, UploadedFile } from '../services/fileService';
import ConfirmDialog from './ConfirmDialog';
import FilePreviewModal from './FilePreviewModal';
import '../styles/DocumentGallery.css';

interface DocumentGalleryProps {
  workspaceId: string;
  entityType: string;
  entityId: string;
  fileType?: string;
  acceptTypes?: string;
  helpText?: string;
  title?: string;
}

const DocumentGallery: React.FC<DocumentGalleryProps> = ({
  workspaceId,
  entityType,
  entityId,
  fileType = 'document',
  acceptTypes = '.pdf,.doc,.docx',
  helpText,
  title
}) => {
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editFileData, setEditFileData] = useState<{ id: string; name: string; extension: string; purchaserCanAccess: boolean } | null>(null);
  const [saving, setSaving] = useState(false);

  // Helper function to split filename and extension
  const splitFileName = (fileName: string): { name: string; extension: string } => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return { name: fileName, extension: '' };
    }
    return {
      name: fileName.substring(0, lastDotIndex),
      extension: fileName.substring(lastDotIndex)
    };
  };

  useEffect(() => {
    fetchDocuments();
  }, [workspaceId, entityType, entityId, fileType]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const files = await getFiles(workspaceId, {
        entityType,
        entityId,
        fileType
      });
      setDocuments(files);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      const file = files[0];

      const uploadedFile = await uploadFile({
        workspaceId,
        file,
        entityType,
        entityId,
        fileType
      });

      setDocuments(prev => [...prev, uploadedFile]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      console.error('Error uploading file:', err);
      toast.error(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      setDeleting(true);
      await deleteFile(workspaceId, fileId);
      setDocuments(prev => prev.filter(f => f._id !== fileId));
      setDeleteFileId(null);
    } catch (err: any) {
      console.error('Error deleting file:', err);
      toast.error(err.response?.data?.message || 'Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveFile = async () => {
    if (!editFileData || !editFileData.name.trim()) {
      toast.error('Please enter a valid file name');
      return;
    }

    try {
      setSaving(true);
      // Combine the name with the original extension
      const fullName = editFileData.name.trim() + editFileData.extension;
      const updatedFile = await updateFile(workspaceId, editFileData.id, {
        title: fullName,
        purchaserCanAccess: editFileData.purchaserCanAccess
      });
      setDocuments(prev =>
        prev.map(f => (f._id === editFileData.id ? { ...f, title: updatedFile.title, purchaserCanAccess: updatedFile.purchaserCanAccess } : f))
      );
      setEditFileData(null);
      toast.success('Document updated successfully');
    } catch (err: any) {
      console.error('Error updating file:', err);
      toast.error(err.response?.data?.message || 'Failed to update file');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewFile = (file: UploadedFile) => {
    setPreviewFile(file);
    setPreviewModalOpen(true);
  };

  const handleDownloadFile = async (file: UploadedFile) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(file.url, '_blank');
    }
  };

  const isPreviewable = (mimeType: string) => {
    return mimeType.startsWith('image/') || mimeType === 'application/pdf';
  };

  return (
    <>
      <div className="document-gallery">
        <div className="document-gallery-header">
          {title && (
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#48182F' }}>{title}</h3>
          )}
          {helpText && (
            <p className="document-gallery-help">{helpText}</p>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Add'}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        <div className="document-gallery-content">
          {loading ? (
            <p className="document-gallery-empty">Loading documents...</p>
          ) : documents.length > 0 ? (
            <div className="document-gallery-list">
              {documents.map((file) => (
                <div key={file._id} className="document-gallery-item">
                  <div className="file-info">
                    <span className="file-name" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={18} /> {file.title || file.originalName}
                    </span>
                    <span className="file-meta">
                      {(file.size / 1024).toFixed(1)} KB • {new Date(file.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {file.purchaserCanAccess && (
                    <div style={{
                      color: '#dc2626',
                      fontSize: '13px',
                      fontWeight: '500',
                      padding: '4px 12px',
                      backgroundColor: '#fef2f2',
                      borderRadius: '4px',
                      border: '1px solid #fecaca',
                      marginRight: '16px'
                    }}>
                      Purchaser Can Access
                    </div>
                  )}
                  <div className="file-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        const displayName = file.title || file.originalName;
                        const { name, extension } = splitFileName(displayName);
                        setEditFileData({ id: file._id, name, extension, purchaserCanAccess: file.purchaserCanAccess || false });
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    {isPreviewable(file.mimeType) && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handlePreviewFile(file)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        title="Preview"
                      >
                        <Eye size={16} />
                      </button>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleDownloadFile(file)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                      title="Download"
                    >
                      <Download size={16} />
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setDeleteFileId(file._id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#dc2626' }}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="document-gallery-empty">No documents found</p>
          )}
        </div>
      </div>

      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          isOpen={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          fileUrl={previewFile.url}
          fileName={previewFile.originalName}
          mimeType={previewFile.mimeType}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteFileId !== null}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        onConfirm={() => deleteFileId && handleDeleteFile(deleteFileId)}
        onClose={() => !deleting && setDeleteFileId(null)}
        loading={deleting}
      />

      {/* Edit Document Modal */}
      {editFileData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => setEditFileData(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
              Edit Document
            </h3>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
              Document Name :
            </label>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', border: '1px solid #D1D5DB', borderRadius: '6px', overflow: 'hidden' }}>
              <input
                type="text"
                value={editFileData.name}
                onChange={(e) => setEditFileData({ ...editFileData, name: e.target.value })}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px'
                }}
                placeholder="Enter file name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveFile();
                  }
                }}
              />
              <span style={{
                padding: '10px 16px',
                backgroundColor: '#F3F4F6',
                color: '#6B7280',
                fontSize: '14px',
                borderLeft: '1px solid #D1D5DB'
              }}>
                {editFileData.extension}
              </span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={editFileData.purchaserCanAccess}
                onChange={(e) => setEditFileData({ ...editFileData, purchaserCanAccess: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              Purchaser can access
            </label>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setEditFileData(null)}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveFile}
                disabled={saving || !editFileData.name.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving || !editFileData.name.trim() ? 'not-allowed' : 'pointer',
                  opacity: saving || !editFileData.name.trim() ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentGallery;
